import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ArrowRight, Building2, Users, FileCheck, BarChart3, Shield, Zap, Star, TrendingUp } from 'lucide-react';
import Button from '../../components/ui/Button';
import SEO from '../../components/SEO';

const STRUCTURED_DATA = {
  "@context": "https://schema.org",
  "@graph": [
  {
    "@type": "SoftwareApplication",
    "name": "ADU Flow",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "url": "https://aduconstr7921.builtwithrocket.new",
    "description": "ADU Flow is the leading CRM and project management platform for ADU construction companies. Manage permits, contractors, customers, and projects in one place.",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "description": "Free trial available"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "340"
    }
  },
  {
    "@type": "Organization",
    "name": "ADU Flow",
    "url": "https://aduconstr7921.builtwithrocket.new",
    "logo": "https://img.rocket.new/generatedImages/rocket_gen_img_11f258838-1764653659093.png",
    "description": "ADU construction CRM and project management software for accessory dwelling unit builders.",
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer support",
      "url": "https://aduconstr7921.builtwithrocket.new/contact-us"
    }
  },
  {
    "@type": "WebSite",
    "name": "ADU Flow",
    "url": "https://aduconstr7921.builtwithrocket.new",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://aduconstr7921.builtwithrocket.new/sign-up"
    }
  }]

};

const LandingPage = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleStartFreeTrial = () => {
    localStorage.removeItem('demo_mode');
    navigate('/sign-up');
  };

  const handleLogin = () => {
    localStorage.removeItem('demo_mode');
    navigate('/login');
  };

  const handleTryDemo = () => {
    navigate('/login?demo=true');
  };

  const testimonials = [
  {
    name: 'Marcus Chen',
    role: 'Owner, Pacific ADU Builders',
    quote: 'ADU Flow cut our permit tracking time in half. We went from missing deadlines to having everything automated.',
    rating: 5,
    initials: 'MC'
  },
  {
    name: 'Sarah Delgado',
    role: 'Project Manager, SoCal ADU Co.',
    quote: 'Managing 20+ projects used to be chaos. Now I have real-time visibility into every stage of every build.',
    rating: 5,
    initials: 'SD'
  },
  {
    name: 'James Okafor',
    role: 'CEO, Bay Area ADU Group',
    quote: 'The contractor management module alone saved us thousands in miscommunication costs. Worth every penny.',
    rating: 5,
    initials: 'JO'
  }];


  const stats = [
  { value: '340+', label: 'ADU Companies' },
  { value: '12,000+', label: 'Projects Managed' },
  { value: '98%', label: 'On-Time Permits' },
  { value: '4.9★', label: 'Average Rating' }];


  return (
    <div className="min-h-screen" style={{ fontFamily: "'DM Sans', sans-serif", backgroundColor: '#F8F9FC' }}>
      <SEO
        title="ADU Construction CRM & Project Management Software"
        description="ADU Flow is the leading CRM for ADU construction companies. Streamline permit tracking, contractor management, project pipelines, and customer relationships — all in one platform. Trusted by 340+ ADU builders."
        keywords="ADU CRM, accessory dwelling unit software, ADU construction management, permit tracking software, contractor management, ADU project management, ADU builder software, construction CRM"
        canonical="/"
        structuredData={STRUCTURED_DATA} />
      {/* Google Font */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&display=swap');
        .hero-gradient { background: linear-gradient(135deg, #0D2137 0%, #1B3A5C 50%, #1e4a73 100%); }
        .orange-gradient { background: linear-gradient(135deg, #E8732A 0%, #f08040 100%); }
        .card-hover { transition: transform 0.25s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.25s ease; }
        .card-hover:hover { transform: translateY(-4px); box-shadow: 0 20px 40px rgba(27,58,92,0.15); }
        .bento-card { border-radius: 20px; overflow: hidden; }
        .nav-link { position: relative; transition: color 0.25s ease; }
        .nav-link::after { content: ''; position: absolute; bottom: -4px; left: 0; width: 0; height: 2px; background: #E8732A; transition: width 0.3s ease; border-radius: 2px; }
        .nav-link:hover { color: #E8732A !important; }
        .nav-link:hover::after { width: 100%; }
        .nav-btn-link { position: relative; transition: color 0.25s ease; }
        .nav-btn-link::after { content: ''; position: absolute; bottom: -4px; left: 0; width: 0; height: 2px; background: #E8732A; transition: width 0.3s ease; border-radius: 2px; }
        .nav-btn-link:hover { color: #E8732A !important; }
        .nav-btn-link:hover::after { width: 100%; }
        .stat-number { background: linear-gradient(135deg, #E8732A, #f08040); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
        .shine-border { border: 1px solid rgba(232,115,42,0.3); }
        @keyframes float { 0%,100% { transform: translateY(0px); } 50% { transform: translateY(-8px); } }
        .float-anim { animation: float 4s ease-in-out infinite; }
        @keyframes pulse-ring { 0% { transform: scale(0.95); opacity: 0.7; } 70% { transform: scale(1.05); opacity: 0; } 100% { transform: scale(0.95); opacity: 0; } }
        .pulse-ring::before { content: ''; position: absolute; inset: -6px; border-radius: 50%; border: 2px solid #E8732A; animation: pulse-ring 2s ease-out infinite; }
      `}</style>
      {/* Header */}
      <header
        className="sticky top-0 z-50 transition-all duration-300"
        style={{
          backgroundColor: scrolled ? 'rgba(255,255,255,0.97)' : '#0D2137',
          backdropFilter: scrolled ? 'blur(12px)' : 'none',
          boxShadow: scrolled ? '0 1px 20px rgba(27,58,92,0.1)' : 'none',
          borderBottom: scrolled ? '1px solid rgba(27,58,92,0.08)' : 'none'
        }}>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-18 py-3">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <img
                src="/assets/images/adu_flow_logo.png"
                alt="ADU Flow logo - navy blue and orange house construction icon"
                className="h-12 w-auto object-contain" />
              
            </div>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-6">
              <a href="#features" className="nav-link text-sm font-medium" style={{ color: scrolled ? '#1B3A5C' : '#ffffff' }}>Features</a>
              <a href="#social-proof" className="nav-link text-sm font-medium" style={{ color: scrolled ? '#1B3A5C' : '#ffffff' }}>Reviews</a>
              <a href="#pricing" className="nav-link text-sm font-medium" style={{ color: scrolled ? '#1B3A5C' : '#ffffff' }}>Pricing</a>
              <a href="/contact-us" className="nav-link text-sm font-medium" style={{ color: scrolled ? '#1B3A5C' : '#ffffff' }}>Contact</a>
              <button
                onClick={handleLogin}
                className="nav-btn-link text-sm font-medium transition-colors"
                style={{ color: scrolled ? '#1B3A5C' : '#ffffff' }}>
                
                Sign In
              </button>
              <button
                onClick={handleStartFreeTrial}
                className="text-sm font-semibold px-5 py-2.5 rounded-xl transition-all duration-200 hover:opacity-90 hover:shadow-lg"
                style={{ background: 'linear-gradient(135deg, #E8732A, #f08040)', color: '#ffffff' }}>
                
                Start Free Trial
              </button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 rounded-lg"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              style={{ color: scrolled ? '#1B3A5C' : '#ffffff' }}>
              
              <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                {isMenuOpen ?
                <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></> :
                <><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></>
                }
              </svg>
            </button>
          </div>

          {/* Mobile Nav */}
          {isMenuOpen &&
          <div className="md:hidden py-4 border-t" style={{ borderColor: 'rgba(27,58,92,0.1)' }}>
              <nav className="flex flex-col gap-3">
                <a href="#features" className="text-sm font-medium py-2 px-1 rounded-lg transition-colors hover:text-orange-500" style={{ color: '#1B3A5C' }} onClick={() => setIsMenuOpen(false)}>Features</a>
                <a href="#social-proof" className="text-sm font-medium py-2 px-1 rounded-lg transition-colors hover:text-orange-500" style={{ color: '#1B3A5C' }} onClick={() => setIsMenuOpen(false)}>Reviews</a>
                <a href="#pricing" className="text-sm font-medium py-2 px-1 rounded-lg transition-colors hover:text-orange-500" style={{ color: '#1B3A5C' }} onClick={() => setIsMenuOpen(false)}>Pricing</a>
                <a href="/contact-us" className="text-sm font-medium py-2 px-1 rounded-lg transition-colors hover:text-orange-500" style={{ color: '#1B3A5C' }} onClick={() => setIsMenuOpen(false)}>Contact</a>
                <button onClick={handleLogin} className="text-sm font-medium text-left py-2 px-1 rounded-lg transition-colors hover:text-orange-500" style={{ color: '#1B3A5C' }}>Sign In</button>
                <button
                onClick={handleStartFreeTrial}
                className="text-sm font-semibold px-5 py-3 rounded-xl text-white w-full mt-1 hover:opacity-90 transition-opacity"
                style={{ background: 'linear-gradient(135deg, #E8732A, #f08040)' }}>
                
                  Start Free Trial
                </button>
              </nav>
            </div>
          }
        </div>
      </header>
      {/* Hero Section */}
      <section className="hero-gradient relative overflow-hidden" style={{ minHeight: '92vh', display: 'flex', alignItems: 'center' }}>
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #E8732A, transparent)' }} />
          <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #E8732A, transparent)' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full opacity-5"
          style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)', backgroundSize: '40px 40px' }} />
          
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 relative z-10 w-full">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Copy */}
            <div>
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold mb-8"
                style={{ background: 'rgba(232,115,42,0.15)', color: '#f08040', border: '1px solid rgba(232,115,42,0.3)' }}>
                
                <span className="w-2 h-2 rounded-full bg-orange-400 inline-block" style={{ animation: 'pulse 2s infinite' }} />
                Built for ADU Construction Companies
              </div>

              <h1
                className="font-bold leading-tight mb-6"
                style={{ fontSize: 'clamp(2.4rem, 5vw, 3.8rem)', color: '#ffffff', letterSpacing: '-0.02em' }}>
                
                Run Every ADU Project
                <span className="block" style={{ color: '#E8732A' }}>Without the Chaos</span>
              </h1>

              <p className="text-lg mb-10 leading-relaxed" style={{ color: 'rgba(255,255,255,0.75)', maxWidth: '520px' }}>
                ADU Flow is the only CRM built from the ground up for ADU contractors. Track permits, manage customers, coordinate crews, and close more projects — all in one place.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <button
                  onClick={handleStartFreeTrial}
                  className="flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-semibold text-base transition-all duration-200 hover:shadow-2xl hover:scale-105"
                  style={{ background: 'linear-gradient(135deg, #E8732A, #f08040)', color: '#ffffff', boxShadow: '0 8px 30px rgba(232,115,42,0.4)' }}>
                  
                  Start Free — First Month On Us
                  <ArrowRight size={18} />
                </button>
                <button
                  onClick={handleTryDemo}
                  className="flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-semibold text-base transition-all duration-200 hover:bg-white/10"
                  style={{ border: '1px solid rgba(255,255,255,0.25)', color: '#ffffff' }}>
                  
                  View Live Demo
                </button>
              </div>

              <div className="flex items-center gap-6 flex-wrap">
                <div className="flex items-center gap-2">
                  <Check size={16} style={{ color: '#E8732A' }} />
                  <span className="text-sm" style={{ color: 'rgba(255,255,255,0.65)' }}>No credit card required</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check size={16} style={{ color: '#E8732A' }} />
                  <span className="text-sm" style={{ color: 'rgba(255,255,255,0.65)' }}>$29/month after trial</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check size={16} style={{ color: '#E8732A' }} />
                  <span className="text-sm" style={{ color: 'rgba(255,255,255,0.65)' }}>Cancel anytime</span>
                </div>
              </div>
            </div>

            {/* Right: Dashboard Preview Card */}
            <div className="hidden lg:block">
              <div
                className="float-anim rounded-2xl p-1"
                style={{ background: 'linear-gradient(135deg, rgba(232,115,42,0.4), rgba(255,255,255,0.1))', boxShadow: '0 40px 80px rgba(0,0,0,0.4)' }}>
                
                <div className="rounded-2xl overflow-hidden" style={{ background: '#0D2137' }}>
                  {/* Fake browser bar */}
                  <div className="flex items-center gap-2 px-4 py-3" style={{ background: 'rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    <div className="w-3 h-3 rounded-full" style={{ background: '#ff5f57' }} />
                    <div className="w-3 h-3 rounded-full" style={{ background: '#febc2e' }} />
                    <div className="w-3 h-3 rounded-full" style={{ background: '#28c840' }} />
                    <div className="flex-1 mx-4 px-3 py-1 rounded-md text-xs" style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.4)' }}>app.aduflow.com/dashboard</div>
                  </div>
                  {/* Dashboard content */}
                  <div className="p-5 space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                      {[
                      { label: 'Active Projects', value: '14', icon: Building2, color: '#E8732A' },
                      { label: 'Permits Pending', value: '6', icon: FileCheck, color: '#3b82f6' },
                      { label: 'Revenue MTD', value: '$84K', icon: TrendingUp, color: '#10b981' }]?.
                      map((stat, i) =>
                      <div key={i} className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.06)' }}>
                          <stat.icon size={16} style={{ color: stat?.color }} className="mb-2" />
                          <div className="text-lg font-bold text-white">{stat?.value}</div>
                          <div className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>{stat?.label}</div>
                        </div>
                      )}
                    </div>
                    <div className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.06)' }}>
                      <div className="text-xs font-semibold mb-3" style={{ color: 'rgba(255,255,255,0.5)' }}>RECENT PROJECTS</div>
                      {[
                      { name: 'Johnson ADU — Pasadena', status: 'Permits', pct: 65 },
                      { name: 'Rivera Backyard Suite', status: 'Framing', pct: 40 },
                      { name: 'Chen Garage Conversion', status: 'Complete', pct: 100 }]?.
                      map((p, i) =>
                      <div key={i} className="flex items-center gap-3 mb-2">
                          <div className="flex-1">
                            <div className="text-xs text-white mb-1">{p?.name}</div>
                            <div className="h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.1)' }}>
                              <div
                              className="h-1.5 rounded-full"
                              style={{ width: `${p?.pct}%`, background: p?.pct === 100 ? '#10b981' : 'linear-gradient(90deg, #E8732A, #f08040)' }} />
                            
                            </div>
                          </div>
                          <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(232,115,42,0.2)', color: '#f08040' }}>{p?.status}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Stats Bar */}
      <section style={{ background: '#ffffff', borderBottom: '1px solid #eef0f5' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats?.map((stat, i) =>
            <div key={i} className="text-center">
                <div className="text-3xl font-bold mb-1 stat-number">{stat?.value}</div>
                <div className="text-sm font-medium" style={{ color: '#6b7280' }}>{stat?.label}</div>
              </div>
            )}
          </div>
        </div>
      </section>
      {/* Features — Bento Grid */}
      <section id="features" className="py-24 px-4 sm:px-6 lg:px-8" style={{ background: '#F8F9FC' }}>
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold mb-5"
              style={{ background: 'rgba(232,115,42,0.1)', color: '#E8732A', border: '1px solid rgba(232,115,42,0.2)' }}>
              
              Platform Features
            </div>
            <h2 className="text-4xl font-bold mb-4" style={{ color: '#0D2137', letterSpacing: '-0.02em' }}>
              Everything your ADU business needs
            </h2>
            <p className="text-lg" style={{ color: '#6b7280', maxWidth: '520px' }}>
              Stop juggling spreadsheets, texts, and sticky notes. ADU Flow brings your entire operation into one intelligent platform.
            </p>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
            {/* Large card — Project Management */}
            <div
              className="md:col-span-7 bento-card card-hover p-8 relative"
              style={{ background: 'linear-gradient(135deg, #0D2137 0%, #1B3A5C 100%)', minHeight: '320px' }}>
              
              <div className="absolute top-0 right-0 w-48 h-48 opacity-10" style={{ background: 'radial-gradient(circle, #E8732A, transparent)' }} />
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ background: 'rgba(232,115,42,0.2)' }}>
                <Building2 size={24} style={{ color: '#E8732A' }} />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Project Pipeline</h3>
              <p className="text-base mb-6" style={{ color: 'rgba(255,255,255,0.65)', maxWidth: '360px' }}>
                Visual kanban board tracks every ADU from initial consultation through final inspection. Never lose a project in the shuffle again.
              </p>
              <div className="flex gap-3 flex-wrap">
                {['Planning', 'Permits', 'Framing', 'Finishing', 'Complete']?.map((phase, i) =>
                <span key={i} className="text-xs px-3 py-1.5 rounded-full font-medium"
                style={{ background: i === 1 ? 'rgba(232,115,42,0.3)' : 'rgba(255,255,255,0.1)', color: i === 1 ? '#f08040' : 'rgba(255,255,255,0.7)' }}>
                  {phase}</span>
                )}
              </div>
            </div>

            {/* Permit Tracking */}
            <div
              className="md:col-span-5 bento-card card-hover p-8"
              style={{ background: '#ffffff', border: '1px solid #eef0f5' }}>
              
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ background: 'rgba(27,58,92,0.08)' }}>
                <FileCheck size={24} style={{ color: '#1B3A5C' }} />
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ color: '#0D2137' }}>Permit Tracking</h3>
              <p className="text-sm mb-5" style={{ color: '#6b7280' }}>
                Automated deadline reminders, document storage, and compliance checklists keep every permit on track.
              </p>
              <div className="space-y-2">
                {['Zoning approval', 'Building permit', 'Electrical inspection', 'Final sign-off']?.map((item, i) =>
                <div key={i} className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: i < 2 ? '#E8732A' : 'rgba(27,58,92,0.1)' }}>
                    
                      {i < 2 && <Check size={10} color="white" />}
                    </div>
                    <span className="text-sm" style={{ color: i < 2 ? '#0D2137' : '#9ca3af' }}>{item}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Customer CRM */}
            <div
              className="md:col-span-4 bento-card card-hover p-8"
              style={{ background: '#ffffff', border: '1px solid #eef0f5' }}>
              
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ background: 'rgba(232,115,42,0.1)' }}>
                <Users size={24} style={{ color: '#E8732A' }} />
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ color: '#0D2137' }}>Customer CRM</h3>
              <p className="text-sm" style={{ color: '#6b7280' }}>
                Full customer profiles with property details, zoning data, communication history, and project preferences.
              </p>
            </div>

            {/* Contractor Coordination */}
            <div
              className="md:col-span-4 bento-card card-hover p-8"
              style={{ background: 'linear-gradient(135deg, #E8732A 0%, #f08040 100%)' }}>
              
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ background: 'rgba(255,255,255,0.2)' }}>
                <Shield size={24} color="white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Contractor Hub</h3>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>
                Manage subs, track availability, assign to projects, and monitor performance — all in one place.
              </p>
            </div>

            {/* Financial Insights */}
            <div
              className="md:col-span-4 bento-card card-hover p-8"
              style={{ background: '#ffffff', border: '1px solid #eef0f5' }}>
              
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ background: 'rgba(27,58,92,0.08)' }}>
                <BarChart3 size={24} style={{ color: '#1B3A5C' }} />
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ color: '#0D2137' }}>Financial Reports</h3>
              <p className="text-sm" style={{ color: '#6b7280' }}>
                Revenue tracking, project budgets, and profitability reports that actually make sense.
              </p>
            </div>

            {/* Team Collaboration — wide */}
            <div
              className="md:col-span-12 bento-card card-hover p-8 flex flex-col md:flex-row items-start md:items-center gap-8"
              style={{ background: '#ffffff', border: '1px solid #eef0f5' }}>
              
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(232,115,42,0.1)' }}>
                  <Zap size={24} style={{ color: '#E8732A' }} />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold mb-2" style={{ color: '#0D2137' }}>Team Collaboration & Role-Based Access</h3>
                <p className="text-sm" style={{ color: '#6b7280', maxWidth: '600px' }}>
                  Give every team member the right level of access. Project managers, field crews, and office staff each see exactly what they need — nothing more, nothing less.
                </p>
              </div>
              <div className="flex-shrink-0 flex gap-3">
                {['Admin', 'PM', 'Field', 'Office']?.map((role, i) =>
                <div key={i} className="text-center">
                    <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold mb-1"
                    style={{ background: i === 0 ? '#1B3A5C' : i === 1 ? '#E8732A' : 'rgba(27,58,92,0.1)', color: i < 2 ? 'white' : '#1B3A5C' }}>
                    {role?.[0]}</div>
                    <span className="text-xs" style={{ color: '#9ca3af' }}>{role}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Social Proof / Testimonials */}
      <section id="social-proof" className="py-24 px-4 sm:px-6 lg:px-8" style={{ background: '#ffffff' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold mb-5"
              style={{ background: 'rgba(232,115,42,0.1)', color: '#E8732A', border: '1px solid rgba(232,115,42,0.2)' }}>
              
              Customer Stories
            </div>
            <h2 className="text-4xl font-bold mb-4" style={{ color: '#0D2137', letterSpacing: '-0.02em' }}>
              ADU contractors trust ADU Flow
            </h2>
            <p className="text-lg" style={{ color: '#6b7280' }}>Real results from real construction companies.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials?.map((t, i) =>
            <div
              key={i}
              className="card-hover rounded-2xl p-8"
              style={{ background: '#F8F9FC', border: '1px solid #eef0f5' }}>
              
                <div className="flex gap-1 mb-5">
                  {Array(t?.rating)?.fill(0)?.map((_, j) =>
                <Star key={j} size={16} fill="#E8732A" style={{ color: '#E8732A' }} />
                )}
                </div>
                <p className="text-base mb-6 leading-relaxed" style={{ color: '#374151' }}>
                  &ldquo;{t?.quote}&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
                  style={{ background: i === 0 ? '#1B3A5C' : i === 1 ? '#E8732A' : '#0D2137' }}>
                  
                    {t?.initials}
                  </div>
                  <div>
                    <div className="text-sm font-semibold" style={{ color: '#0D2137' }}>{t?.name}</div>
                    <div className="text-xs" style={{ color: '#9ca3af' }}>{t?.role}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
      {/* Pricing Section */}
      <section id="pricing" className="py-24 px-4 sm:px-6 lg:px-8" style={{ background: '#F8F9FC' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold mb-5"
              style={{ background: 'rgba(232,115,42,0.1)', color: '#E8732A', border: '1px solid rgba(232,115,42,0.2)' }}>
              
              Simple Pricing
            </div>
            <h2 className="text-4xl font-bold mb-4" style={{ color: '#0D2137', letterSpacing: '-0.02em' }}>
              One plan. Everything included.
            </h2>
            <p className="text-lg" style={{ color: '#6b7280' }}>No tiers, no hidden fees, no surprises.</p>
          </div>

          <div className="grid md:grid-cols-5 gap-8 items-center">
            {/* Pricing Card */}
            <div
              className="md:col-span-2 rounded-2xl p-8 relative overflow-hidden"
              style={{ background: 'linear-gradient(135deg, #0D2137 0%, #1B3A5C 100%)', boxShadow: '0 30px 60px rgba(13,33,55,0.3)' }}>
              
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-20" style={{ background: 'radial-gradient(circle, #E8732A, transparent)' }} />
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-6"
                style={{ background: 'rgba(232,115,42,0.25)', color: '#f08040' }}>
                
                🎉 First Month Free
              </div>
              <div className="mb-6">
                <div className="flex items-end gap-2">
                  <span className="text-6xl font-bold text-white">$29</span>
                  <span className="text-lg mb-2" style={{ color: 'rgba(255,255,255,0.6)' }}>/mo</span>
                </div>
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.55)' }}>per user · billed monthly</p>
              </div>
              <button
                onClick={handleStartFreeTrial}
                className="w-full py-4 rounded-xl font-semibold text-base transition-all duration-200 hover:opacity-90 hover:shadow-xl mb-4"
                style={{ background: 'linear-gradient(135deg, #E8732A, #f08040)', color: '#ffffff' }}>
                
                Start Free Trial
              </button>
              <p className="text-xs text-center" style={{ color: 'rgba(255,255,255,0.4)' }}>No credit card required</p>
            </div>

            {/* Features list */}
            <div className="md:col-span-3">
              <p className="text-sm font-semibold mb-6" style={{ color: '#9ca3af', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Everything included</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                'Unlimited projects',
                'Unlimited customers',
                'Unlimited team members',
                'Full permit tracking',
                'Contractor management',
                'Financial reporting',
                'Calendar & scheduling',
                'Role-based access',
                'Mobile-friendly',
                'Priority support',
                'Data export',
                'Secure cloud storage']?.
                map((feature, i) =>
                <div key={i} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(232,115,42,0.15)' }}>
                      <Check size={12} style={{ color: '#E8732A' }} />
                    </div>
                    <span className="text-sm font-medium" style={{ color: '#374151' }}>{feature}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Final CTA */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 hero-gradient relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-64 h-64 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #E8732A, transparent)' }} />
          <div className="absolute bottom-0 right-1/4 w-48 h-48 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #E8732A, transparent)' }} />
        </div>
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <div className="flex justify-center mb-8">
            <img
              src="/assets/images/adu_flow_logo.png"
              alt="ADU Flow logo"
              className="h-20 w-auto object-contain"
              style={{ filter: 'brightness(0) invert(1)' }} />
            
          </div>
          <h2 className="text-4xl font-bold text-white mb-5" style={{ letterSpacing: '-0.02em' }}>
            Ready to run a tighter ADU operation?
          </h2>
          <p className="text-lg mb-10" style={{ color: 'rgba(255,255,255,0.7)' }}>
            Join hundreds of ADU contractors who've replaced their spreadsheets with ADU Flow. Your first month is completely free.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleStartFreeTrial}
              className="flex items-center justify-center gap-2 px-10 py-4 rounded-xl font-semibold text-base transition-all duration-200 hover:shadow-2xl hover:scale-105"
              style={{ background: 'linear-gradient(135deg, #E8732A, #f08040)', color: '#ffffff', boxShadow: '0 8px 30px rgba(232,115,42,0.4)' }}>
              
              Get Started Free
              <ArrowRight size={18} />
            </button>
            <button
              onClick={handleTryDemo}
              className="flex items-center justify-center gap-2 px-10 py-4 rounded-xl font-semibold text-base transition-all duration-200 hover:bg-white/10"
              style={{ border: '1px solid rgba(255,255,255,0.25)', color: '#ffffff' }}>
              
              Try Demo First
            </button>
          </div>
          <p className="text-sm mt-6" style={{ color: 'rgba(255,255,255,0.45)' }}>
            First month free · $29/month after · No setup fees · Cancel anytime
          </p>
        </div>
      </section>
      {/* Footer */}
      <footer style={{ background: '#0D2137', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row justify-between items-start gap-10">
            <div className="max-w-xs">
              <img
                src="/assets/images/adu_flow_logo.png"
                alt="ADU Flow"
                className="h-14 w-auto object-contain mb-4"
                style={{ filter: 'brightness(0) invert(1)' }} />
              
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>
                The complete CRM platform built for ADU construction companies.
              </p>
            </div>
            <div className="flex gap-16 flex-wrap">
              <div>
                <h4 className="text-sm font-semibold text-white mb-4">Product</h4>
                <ul className="space-y-3">
                  <li><a href="#features" className="text-sm transition-colors hover:text-white" style={{ color: 'rgba(255,255,255,0.45)' }}>Features</a></li>
                  <li><a href="#pricing" className="text-sm transition-colors hover:text-white" style={{ color: 'rgba(255,255,255,0.45)' }}>Pricing</a></li>
                  <li><button onClick={handleTryDemo} className="text-sm transition-colors hover:text-white" style={{ color: 'rgba(255,255,255,0.45)' }}>Demo</button></li>
                </ul>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-white mb-4">Account</h4>
                <ul className="space-y-3">
                  <li><button onClick={handleLogin} className="text-sm transition-colors hover:text-white" style={{ color: 'rgba(255,255,255,0.45)' }}>Sign In</button></li>
                  <li><button onClick={handleStartFreeTrial} className="text-sm transition-colors hover:text-white" style={{ color: 'rgba(255,255,255,0.45)' }}>Sign Up</button></li>
                </ul>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-white mb-4">Legal</h4>
                <ul className="space-y-3">
                  <li><a href="/privacy-policy" className="text-sm transition-colors hover:text-white" style={{ color: 'rgba(255,255,255,0.45)' }}>Privacy</a></li>
                  <li><a href="/terms-of-use" className="text-sm transition-colors hover:text-white" style={{ color: 'rgba(255,255,255,0.45)' }}>Terms</a></li>
                  <li><a href="/contact-us" className="text-sm transition-colors hover:text-white" style={{ color: 'rgba(255,255,255,0.45)' }}>Contact Us</a></li>
                </ul>
              </div>
            </div>
          </div>
          <div className="mt-10 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>&copy; 2026 ADU Flow. All rights reserved.</p>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>Built for ADU construction professionals</p>
          </div>
        </div>
      </footer>
    </div>
  );

};

export default LandingPage;